import React, { useEffect, useState } from 'react';
import { useOrg } from '../contexts/OrgContext';
import { supabase } from '../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Download, Filter, TrendingDown, TrendingUp, Coins } from 'lucide-react';

interface Transaction {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  created_at: string | null;
  user_id: string;
}

const TYPE_LABELS: Record<string, string> = {
  generation_cost: 'Geração IA',
  purchase: 'Compra',
  referral_bonus: 'Indicação',
  plan_renewal: 'Renovação do plano',
  adjustment: 'Ajuste',
  marketplace_purchase: 'Compra no marketplace',
  marketplace_sale: 'Venda no marketplace',
  subscription_grant: 'Créditos do plano',
  subscription_seat: 'Créditos de assento',
  seat_clawback: 'Estorno de assento',
};

const fmtCredits = (n: number) =>
  n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

const round2 = (n: number) => Math.round(n * 100) / 100;

const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'];

export default function FinancialDashboard() {
  const { org } = useOrg();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!org) return;
    async function fetch() {
      setLoading(true);
      let query = supabase.from('credit_transactions').select('*').eq('org_id', org!.id).order('created_at', { ascending: false });
      if (filterType !== 'all') query = query.eq('type', filterType);
      const { data } = await query;
      setTransactions(data || []);
      setLoading(false);
    }
    fetch();
  }, [org, filterType]);

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthTxns = transactions.filter(t => t.created_at && new Date(t.created_at) >= startOfMonth);

  const spent = round2(monthTxns.filter(t => t.amount < 0).reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0));
  const earned = round2(monthTxns.filter(t => t.amount > 0).reduce((s, t) => s + (Number(t.amount) || 0), 0));

  // Pie data
  const byType: Record<string, number> = {};
  monthTxns.forEach(t => {
    const label = TYPE_LABELS[t.type] || t.type;
    byType[label] = round2((byType[label] || 0) + Math.abs(Number(t.amount) || 0));
  });
  const pieData = Object.entries(byType).map(([name, value]) => ({ name, value }));

  // Bar data (daily, last 30 days)
  const barMap: Record<string, number> = {};
  for (let i = 0; i < 30; i++) {
    const d = new Date(now.getTime() - (29 - i) * 86400000);
    barMap[d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })] = 0;
  }
  transactions.forEach(t => {
    if (t.created_at && t.amount < 0) {
      const key = new Date(t.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      if (barMap[key] !== undefined) barMap[key] = round2(barMap[key] + Math.abs(Number(t.amount) || 0));
    }
  });
  const barData = Object.entries(barMap).map(([date, credits]) => ({ date, credits }));

  const pagedTxns = transactions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(transactions.length / PAGE_SIZE);

  const exportCSV = () => {
    const header = 'Data,Tipo,Descrição,Valor\n';
    const rows = transactions.map(t =>
      `${t.created_at ? new Date(t.created_at).toLocaleDateString('pt-BR') : ''},${TYPE_LABELS[t.type] || t.type},${(t.description || '').replace(/,/g, ';')},${fmtCredits(Number(t.amount) || 0)}`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `financeiro_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Dashboard Financeiro</h1>
          <p className="subtitle">Relatórios de consumo e transações</p>
        </div>
        <button className="btn-secondary" onClick={exportCSV}><Download size={16} /> Exportar CSV</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card stat-primary">
          <div className="stat-icon"><Coins size={24} /></div>
          <div className="stat-info">
            <span className="stat-label">Saldo Atual</span>
            <span className="stat-value">{fmtCredits(Number(org?.available_credits) || 0)}</span>
          </div>
        </div>
        <div className="stat-card stat-orange">
          <div className="stat-icon"><TrendingDown size={24} /></div>
          <div className="stat-info">
            <span className="stat-label">Gastos do Mês</span>
            <span className="stat-value">{fmtCredits(spent)}</span>
          </div>
        </div>
        <div className="stat-card stat-green">
          <div className="stat-icon"><TrendingUp size={24} /></div>
          <div className="stat-info">
            <span className="stat-label">Créditos Recebidos</span>
            <span className="stat-value">{fmtCredits(earned)}</span>
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="glass-card chart-card" style={{ flex: 2 }}>
          <h2>Consumo Diário (30 dias)</h2>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => fmtCredits(Number(v))} />
                <Tooltip formatter={(v) => fmtCredits(Number(v))} contentStyle={{ background: '#1a1c23', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                <Bar dataKey="credits" fill="#6366f1" radius={[4, 4, 0, 0]} name="Créditos" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {pieData.length > 0 && (
          <div className="glass-card chart-card" style={{ flex: 1 }}>
            <h2>Por Tipo</h2>
            <div className="chart-wrapper">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(props: any) => `${props.name || ''} ${((props.percent || 0) * 100).toFixed(0)}%`} labelLine={false}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtCredits(Number(v))} contentStyle={{ background: '#1a1c23', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div className="glass-card wide-card">
        <div className="section-header">
          <h2>Transações</h2>
          <div className="filter-bar">
            <Filter size={16} />
            <select className="form-select form-select-sm" value={filterType} onChange={e => { setFilterType(e.target.value); setPage(0); }}>
              <option value="all">Todos</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loading-spinner" /></div>
        ) : (
          <>
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr><th>Data</th><th>Tipo</th><th>Descrição</th><th>Valor</th></tr>
                </thead>
                <tbody>
                  {pagedTxns.map(t => (
                    <tr key={t.id}>
                      <td className="text-muted">{t.created_at ? new Date(t.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                      <td><span className="badge badge-info">{TYPE_LABELS[t.type] || t.type}</span></td>
                      <td>{t.description || '—'}</td>
                      <td className={t.amount < 0 ? 'text-error' : 'text-success'}>{t.amount > 0 ? '+' : ''}{fmtCredits(Number(t.amount) || 0)}</td>
                    </tr>
                  ))}
                  {pagedTxns.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', opacity: 0.5 }}>Nenhuma transação encontrada</td></tr>}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</button>
                <span>{page + 1} / {totalPages}</span>
                <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Próximo</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
